import { useLayoutEffect, useMemo, useCallback, useRef, useEffect } from "react";
import { Box, Breakpoint, Container, Stack, SxProps, Theme, useTheme } from "@mui/material";

import React from "react";
import { useDispatch } from "react-redux";
import { SetPlayerContainerHeight } from "../../Redux/Reducers/redux-slice-layout";
import "./layout-panel.css";
import { useResizable } from "../../Utils/Hooks/useResizable";
export interface LayoutCenterPanelProps {
  children?: React.ReactNode;
  ref?: React.ForwardedRef<HTMLDivElement>;
  withContainer?: boolean;
  maxWidth?: false | Breakpoint | undefined;
  sxContent?: SxProps<Theme>;
  noScroll?: boolean;
  leftPanel: any;
  rightPanel: any;
  setHeadingCollapsed?: any;
  visibleBanner?: boolean;
  Banner?: any;
  hasHeading?: boolean;
  panelHighlight?: boolean;
}

const LayoutCenterPanel: React.FC<LayoutCenterPanelProps> = ({
  children,
  withContainer = false,
  maxWidth = "xl",
  sxContent = {},
  noScroll = false,
  leftPanel,
  rightPanel,
  setHeadingCollapsed,
  visibleBanner = false,
  Banner,
  hasHeading = false,
  panelHighlight = true,
}) => {
  const theme = useTheme();
  const scrollContentRef = React.useRef<HTMLDivElement | null>(null);

  const dispatch = useDispatch();

  const bodytodisplay = useMemo(() => {
    return withContainer ? (
      <Container
        maxWidth={maxWidth}
        sx={{
          mt: visibleBanner && Banner ? "56px" : 0,
          mb: noScroll ? 0 : 4,
          padding: `0 !important`,
        }}
      >
        {children}
      </Container>
    ) : (
      children
    );
  }, [children, maxWidth, noScroll, withContainer, visibleBanner, Banner]);

  // used to determine player height in PlayerWrapper
  const obsCallback = useCallback((event) => {
    dispatch(SetPlayerContainerHeight(event[0].contentBoxSize[0].blockSize));
  }, []);
  const obsRef = useRef<ResizeObserver | undefined>(undefined);

  const resizeRef = useCallback((node) => {
    if (obsRef.current?.disconnect) obsRef.current?.disconnect();
    obsRef.current = new ResizeObserver(obsCallback);
    if (node) obsRef.current.observe(node);
  }, []);

  const [lastScroll, setLastScroll] = React.useState(0);

  const onScroll: EventListener = (e: Event) => {
    if (hasHeading && scrollContentRef.current) {
      const content: HTMLDivElement = scrollContentRef.current as HTMLDivElement;
      const scrollTop = content.scrollTop || 0;
      if (scrollTop < 50) {
        setHeadingCollapsed(false);
        setLastScroll(scrollTop);
      } else {
        const childContent: HTMLDivElement = content.firstChild as HTMLDivElement;

        if (!childContent) return;
        setHeadingCollapsed(true);
      }
    }
  };

  useLayoutEffect(() => {
    if (!scrollContentRef.current) {
      return;
    }

    // The ref value 'scrollContentRef.current' will likely have changed by the time this effect cleanup function runs
    const content = scrollContentRef.current;
    if (hasHeading) {
      content && content.addEventListener("scroll", onScroll, { passive: true });
    }
    return () => {
      content && content.removeEventListener("scroll", onScroll);
    };
  }, [hasHeading, onScroll]);

  useResizable();

  return (
    <Box
      sx={
        panelHighlight
          ? {
              ...theme.helpers(theme).panelHighlights,
              background: theme.palette.background.default,
              paddingTop: "1px",
            }
          : {}
      }
      ml={leftPanel.open ? "8px" : 0}
      mr={rightPanel.open ? "8px" : 0}
      ref={resizeRef}
      className={"center-panel dashboard-content"}
      component="main"
      width={"100%"}
    >
      <Stack display={Banner && visibleBanner ? "block" : "none"} className={"banner-wrapper"}>
        {Banner && visibleBanner && Banner}
      </Stack>
      <Stack
        ref={scrollContentRef}
        direction={"column"}
        className="dashboard-content"
        component="main"
        sx={{
          ...theme.helpers(theme).scrollableContainer,
          ...(sxContent as SxProps<Theme>),
          overflowY: !noScroll ? "auto" : "hidden",
        }}
      >
        {bodytodisplay}
      </Stack>
    </Box>
  );
};

export default LayoutCenterPanel;
